import React from "react";
import { Modal, Row, Col } from "react-bootstrap";
import {
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaClipboardCheck,
  FaCheck,
} from "react-icons/fa";

const BindingDisputeTimelineModal = ({ show, onHide, data }) => {
  // Use timeline from data if available, otherwise fall back to static steps
  const steps =
    data?.timeline?.length > 0
      ? data.timeline.map((item) => ({
          label: item?.label,
          status: item?.status,
          date: item?.date,
        }))
      : [
          { label: "Case Registered", status: "Pending" },
          { label: "Tribunal constituted", status: "Pending" },
          { label: "Claim Filed", status: "Pending" },
          { label: "Defence Filed", status: "Pending" },
          { label: "Primary Hearing", status: "Pending" },
          { label: "Final Hearing", status: "Pending" },
          { label: "Final Award", status: "Pending" },
        ];

  const renderStatusBadge = (status) => {
    let style = {};
    let icon = null;
    const label = status || "N/A";

    switch (status?.toLowerCase()) {
      case "pending":
        style = {
          backgroundColor: "#fff3cd",
          color: "#856404",
          border: "1px solid #ffeeba",
        };
        icon = <FaClock className="me-2" />;
        break;
      case "approved":
        style = {
          backgroundColor: "#d4edda",
          color: "#155724",
          border: "1px solid #c3e6cb",
        };
        icon = <FaCheckCircle className="me-2" />;
        break;
      case "reviewed":
        style = {
          backgroundColor: "#0d6efd", // Bootstrap primary blue
          color: "#fff",
          border: "1px solid #0d6efd",
        };
        icon = <FaClipboardCheck className="me-2" />;
        break;
      case "rejected":
        style = {
          backgroundColor: "#f8d7da",
          color: "#721c24",
          border: "1px solid #f5c6cb",
        };
        icon = <FaTimesCircle className="me-2" />;
        break;
      default:
        style = {
          backgroundColor: "#e2e3e5",
          color: "#383d41",
          border: "1px solid #d6d8db",
        };
        icon = null;
    }

    return (
      <span
        className="badge rounded-pill d-inline-flex align-items-center"
        style={{
          ...style,
          fontSize: "1rem",
          padding: "8px 16px",
          fontWeight: "500",
        }}
      >
        {icon}
        {label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <span className="text-center fs-5">AECCI IAC - Case Timeline</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ padding: "20px" }}>
          <div className="mb-4 border-bottom pb-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              <h5 className="mb-0">Current Status:</h5>
              {renderStatusBadge(data?.status)}
            </div>
            <Row>
              <Col md={6}>
                <p className="mb-1">
                  <strong>Application No:</strong> {data?.appNo || "N/A"}
                </p>
              </Col>
              <Col md={6}>
                <p className="mb-1">
                  <strong>Case ID:</strong> {data?.caseId || "N/A"}
                </p>
              </Col>
            </Row>
          </div>
          <div
            className="position-relative m-3"
            style={{ paddingLeft: "20px", paddingRight: "20px" }}
          >
            {steps.map((step, index) => {
              const isCompleted = step.status === "Completed";
              // Determine if this step is the "current" active one (last completed or first pending?)
              // Visual logic: Blue dot if completed, Gray if pending.
              // Line color depends on if the *next* step is also started/completed?
              // Let's stick to simple: Completed = Blue, Pending = Gray.

              const isLast = index === steps.length - 1;

              return (
                <Row
                  key={index}
                  className="mb-0 position-relative"
                  style={{ minHeight: "60px" }}
                >
                  {/* Left Side: Label */}
                  <Col
                    xs={5}
                    className="text-end pe-4 d-flex align-items-center justify-content-end"
                  >
                    <h6
                      className={`mb-0 fw-bold ${
                        isCompleted ? "text-primary" : "text-muted"
                      }`}
                    >
                      {step.label}
                    </h6>
                  </Col>

                  {/* Center: Stepper Indicator */}
                  <Col
                    xs={2}
                    className="d-flex flex-column align-items-center p-0 position-relative"
                  >
                    {/* Vertical Line */}
                    {!isLast && (
                      <div
                        className="position-absolute"
                        style={{
                          width: "2px",
                          backgroundColor:
                            // Line is blue if THIS step is completed AND the NEXT step is also completed (or maybe just this one?)
                            // Usually line connects completed steps.
                            // If step[i] is completed, line to step[i+1] should be blue ONLY if step[i+1] is also completed?
                            // Or if we are "in progress" to the next step.
                            // Let's make line blue if THIS step is completed.
                            isCompleted ? "#0d6efd" : "#e9ecef",
                          top: "50%",
                          bottom: "-50%",
                          left: "50%",
                          transform: "translateX(-50%)",
                          zIndex: 0,
                        }}
                      ></div>
                    )}

                    {/* Dot */}
                    <div
                      className="rounded-circle d-flex justify-content-center align-items-center"
                      style={{
                        width: isCompleted ? "20px" : "16px",
                        height: isCompleted ? "20px" : "16px",
                        backgroundColor: isCompleted ? "#0d6efd" : "#e9ecef", // Blue for completed
                        border: isCompleted ? "3px solid #b6d4fe" : "none", // Ring for active/completed
                        color: "#fff",
                        zIndex: 1,
                        marginTop: "auto",
                        marginBottom: "auto",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {isCompleted && <FaCheck size={10} />}
                    </div>
                  </Col>

                  {/* Right Side: Date */}
                  <Col
                    xs={5}
                    className="text-start ps-4 d-flex align-items-center"
                  >
                    {step.date ? (
                      <span className="text-muted">
                        {formatDate(step.date)}
                      </span>
                    ) : (
                      <span
                        className="text-muted"
                        style={{ fontStyle: "italic", fontSize: "0.9rem" }}
                      >
                        {isCompleted ? "Date N/A" : "Pending"}
                      </span>
                    )}
                  </Col>
                </Row>
              );
            })}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default BindingDisputeTimelineModal;
